/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import styled, { css } from 'styled-components';

import { FiraSansBold, FiraSansExtraLight, FontWeight, Text } from '../Text';
import Icon from '../Icon';
import Colors from 'constants/Colors';
import Label from './Label';
import ValidationError from './ValidationError';
import { isEqualObject } from 'utils/src/object-util';
import { Events } from 'constants/Events';
import { Breakpoints } from "../../constants/Breakpoints";
import { mediaHelper } from "../../utils/src";

const propTypes = {
  corDiferente: PropTypes.bool,
  weight: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ).isRequired,
  tabIndex: PropTypes.number,
  hideLabelAsterisk: PropTypes.bool,
  isRequired: PropTypes.bool,
  onChange: PropTypes.func,
  optionMapper: PropTypes.func,
  validation: PropTypes.func,
};

const defaultProps = {
  corDiferente: true,
  weight: '700',
  placeholder: '',
  label: null,
  isRequired: false,
  tabIndex: 0,
  hideLabelAsterisk: false,
  validation: true,
  onChange: value => value,
  optionMapper: opt => opt
};

const SelectContainer = styled.div`
  position: relative;
  user-select: none;
  outline: none;
  margin-bottom: 10px;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansExtraLight}
  
  height: 24px;
    
  @media screen and (min-width: ${Breakpoints.sm}) {
    height: 42px;
  }
    
  @media screen and (min-width: ${Breakpoints.md}) {
    margin-bottom: 0;
  }
`;

const SelectedOptionContainer = styled.div`
  height: inherit;
  border: 1px solid ${Colors.GRAY[3]};
  background-color: ${Colors.WHITE};
  border-radius: 4px;
  display: flex;
  align-items: center;
  cursor: pointer;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansBold}

  &:focus {
    border-color: ${Colors.SECONDARY};
  }

  ${({ hasValue }) =>
    hasValue &&
    css`
      
      background-color-old: ${Colors.GRAY[3]};
      background-color: ${Colors.WHITE};
    `}

  ${({ isOpen }) =>
    isOpen &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-color: ${Colors.SECONDARY};
    `}

  ${({ error, isOpen, touched }) =>
    error &&
    !isOpen &&
    touched &&
    css`
      border: 3px solid ${Colors.DANGER};

      & ${FontIcon} {
        color: ${Colors.DANGER};
      }
    `}
`;

const PlaceholderContainer = styled.div`
  margin-left: 4px;
  font-size: 14px;
  line-height: normal;
  flex: 1;
  color: ${Colors.GRAY[0]};
  min-width: 0;
  text-align: ${({ alignCenter }) => (alignCenter ? 'center' : 'initial')};
  ${FiraSansBold}

  color: ${({ corDinamica }) => (corDinamica ? Colors.GRAY[6] : Colors.GRAY[3])};
  
  ${({ hasValue, disabledActive }) =>
    (hasValue || disabledActive) &&
    css`
      color: ${Colors.GRAY[1]};
    `}

  & ${Text} {
    text-transform: capitalize;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const FontIcon = styled(({ isOpen, ...props }) => <Icon {...props} />)`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${Colors.GRAY[0]};
  transition: transform 0.3s ease;

  ${({ isOpen }) =>
    isOpen &&
    css`
      color: ${Colors.SECONDARY};
      transform: rotate(180deg);
    `}
    
  margin: 1px;
  
  @media screen and (min-width: ${Breakpoints.sm}) {
    margin: 10px 6px 10px 10px;
  }
`;

export const OptionsContainer = styled.ul`
  position: absolute;
  z-index: 10;
  left: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 300px;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-left: 1px solid ${Colors.SECONDARY};
  border-right: 1px solid ${Colors.SECONDARY};
  border-bottom: 1px solid ${Colors.SECONDARY};
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  top: 23px;
  
  ${mediaHelper.sm`
    top: 30px;
  `}
`;

export const Option = styled.li`
  list-style: none;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${Colors.GRAY[3]};

  ${({ selected }) =>
    selected &&
    css`
      background-color: ${Colors.Alpha(Colors.SECONDARY, 0.7)};
    `}

  &:hover {
    background-color: ${Colors.GRAY[4]};
  }
`;

const SelectField = ({
  corDiferente,
  weight,
  placeholder,
  label,
  options,
  tabIndex,
  isRequired,
  optionMapper,
  disabled,
  disabledActive,
  isEqual,
  validationMinHeight,
  validation,
  hideLabelAsterisk,
  field,
  form: { setFieldValue, setFieldTouched, touched, errors },
  onChange,
  onSearchSelect,
  searchList,
  ...props
}) => {
  const isClickOrTouchEvent = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const optionsWithPlaceholder = [
    ...(field.value
      ? [{ label: placeholder || '', value: "", isPlaceholder: true }]
      : []),
    ...options
  ];
  const isEqualObjectFunc = isEqual ? isEqual : isEqualObject;

  var touchedList;
  var touchedItem;
  var errorList;
  var errorItem;
  const metaFields = field.name.split('.');
  if (metaFields.length == 3) {
    touchedList = touched[metaFields[0]]
    if (touchedList) {
      touchedItem = touchedList[parseInt(metaFields[1])]
    }
    errorList = errors[metaFields[0]]
    if (errorList) {
      errorItem = errorList[parseInt(metaFields[1])]
    }
  }

  const meta = {

    touched: metaFields.length == 3 && touchedItem ? touchedItem[metaFields[2]] || touched[field.name] : touched[field.name],
    error: metaFields.length == 3 && errorItem ? errorItem[metaFields[2]] || errors[field.name] : errors[field.name],
    isOpen
  };

  const innerOptionMapper = opt =>
    opt.isPlaceholder ? opt : optionMapper(opt);
  const selectedOptionIndex = (optionsWithPlaceholder || []).findIndex(opt =>
    isEqualObjectFunc(innerOptionMapper(opt).value, field.value)
  );
  const selectedOption =
    selectedOptionIndex > -1 && optionsWithPlaceholder[selectedOptionIndex]
      ? innerOptionMapper(optionsWithPlaceholder[selectedOptionIndex])
      : undefined;
  const toggleField = () => {
    if (!disabled) {
      setIsOpen(!meta.isOpen);
    }
  };

  return (
    <div {...props}>
      {label && (
        <Label
          meta={meta}
          isRequired={isRequired}
          weight={FontWeight.Regular}
          hideLabelAsterisk={hideLabelAsterisk}
        >
          {label}
        </Label>
      )}
      <SelectContainer
        data-name={field.name}
        tabIndex={tabIndex}
        onMouseDown={e => {
          isClickOrTouchEvent.current = e.button === Events.MOUSE_KEY_DOWN_CODE;
        }}
        onTouchStart={() => {
          isClickOrTouchEvent.current = true;
        }}
        onClick={toggleField}
        onFocus={() => {
          if (disabled) return;
          setIsOpen(!isClickOrTouchEvent.current);
          isClickOrTouchEvent.current = false;
        }}
        onBlur={() => {
          setFieldTouched(field.name, true, true);
          setIsOpen(false);
          isClickOrTouchEvent.current = false;
        }}
        onKeyDown={e => {
          if (disabled) return;

          switch (e.key.toLowerCase()) {
            case 'enter':
              toggleField();
              break;
          }
        }}
      >
        <SelectedOptionContainer
          {...meta}
          hasValue={selectedOption && selectedOption.value}
        >
          <PlaceholderContainer
            hasValue={selectedOption && selectedOption.value}
            disabledActive={disabledActive}
            corDinamica={corDiferente}
          >
            <Text weight={weight}>
              {(selectedOption && selectedOption.label) || placeholder}
            </Text>
          </PlaceholderContainer>
          <FontIcon name="arrow-down" isOpen={meta.isOpen} />
        </SelectedOptionContainer>
        {(meta.isOpen && !disabled) && (
          <OptionsContainer>
            {optionsWithPlaceholder.map((opt, index) => {
              const mappedOption = innerOptionMapper(opt);
              const hasSelected = isEqualObjectFunc(
                mappedOption.value,
                field.value
              );

              return (
                <Option
                  key={`opt-${field.name}-${index}`}
                  onClick={() => {
                    setIsOpen(false);
                    setFieldValue(field.name, mappedOption.value);
                    onChange(mappedOption.value);
                  }}
                  selected={hasSelected}
                >
                  <Text
                    weight={hasSelected ? FontWeight.Bold : FontWeight.Regular}
                  >
                    {mappedOption.label}
                  </Text>
                </Option>
              );
            })}
          </OptionsContainer>
        )}
      </SelectContainer>
      {validation && (<ValidationError meta={meta} validationMinHeight={validationMinHeight} />)}
    </div>
  );
};

SelectField.propTypes = propTypes;
SelectField.defaultProps = defaultProps;

export default SelectField;
